import {MultiSelect} from "primereact/multiselect";
import {Button} from "primereact/button";
import {useAppDispatch, useAppSelector} from "redux/hooks";
import React, {useRef, useState} from "react";
import {InputNumber} from "primereact/inputnumber";
import {classNames} from "primereact/utils";
import {addQualificationGroupUser, changeQualificationGroupUserLevel, deleteQualificationGroupUser} from "../../../redux/actions/actionsQualificationGroup";
import {checkPermissions} from "../../../redux/permissions/permissionsUtils";
import {getUserDetails} from "../../../redux/api/apiUsersList";
import {setEditType} from "../../../redux/actions/actions";
import UserListDialog from "../../Settings/UsersList/UserDialod";

const QualificationGroupDialogUsers = () => {
    const dispatch = useAppDispatch()
    const qualificationData = useAppSelector(state => state.QualificationGroupListReducer.qualificationData);
    const usersData = useAppSelector(state => state.QualificationGroupListReducer.usersData) as any;
    const selectedCompanyId = useAppSelector(state => state.QualificationGroupListReducer.selectedQualificationGroupId);
    const archiveTableToggle = useAppSelector(state => state.QualificationGroupListReducer.archiveQualificationGroupTableToggle);
    const editType = useAppSelector(state => state.UsersReducer.editType);
    const inputRef = useRef<any>(null);
    const errorFields = useAppSelector(state => state.QualificationGroupListReducer.errorFields);
    const rolesVision = useAppSelector(state => state.RolesReducer.rolesVision) as unknown as Record<string, string[]>;
    const usersManagerPermissions = checkPermissions(rolesVision, 'Users');
    const jwtToken = useAppSelector(state => state.UsersReducer.userData.jwtToken);
    const detailsLoading = useAppSelector(state => state.UsersListReducer.detailLoading);
    const [currentUserId, setCurrentUserId] = useState<any>(null);
    const {
        update: hasUsersManagerUpdatePermission,
        visible: hasUsersManagerVisiblePermission
    } = usersManagerPermissions;

    const sameUserId = (a: any, b: any) =>
        a != null && b != null && String(a) === String(b);

    const isUserInCatalog = (userId: any) =>
        Boolean(
            userId != null &&
            usersData?.some((user: any) => sameUserId(user.userId, userId))
        );

    const mergePreservedSelectedItems = (
        selected: any[],
        existing: any[],
        getId: (item: any) => any,
        isInCatalog: (id: any) => boolean,
    ) => {
        const selectedIds = new Set(
            (selected || [])
                .map(getId)
                .filter((id: any) => id != null)
                .map((id: any) => String(id))
        );
        const result: any[] = [];
        const seen = new Set<string>();

        (existing || []).forEach((item: any) => {
            const id = getId(item);
            if (id == null) {
                return;
            }
            const key = String(id);
            const keep = !isInCatalog(id) || selectedIds.has(key);
            if (keep && !seen.has(key)) {
                seen.add(key);
                result.push(item);
            }
        });

        (selected || []).forEach((item: any) => {
            const id = getId(item);
            if (id == null) {
                return;
            }
            const key = String(id);
            if (!seen.has(key)) {
                seen.add(key);
                result.push(item);
            }
        });

        return result;
    };

    const canOpenUserDetails = (userId: any) =>
        Boolean(hasUsersManagerVisiblePermission && isUserInCatalog(userId));

    const canEditUserDetails = (userId: any) =>
        Boolean(archiveTableToggle && hasUsersManagerUpdatePermission && isUserInCatalog(userId));

    const userDetailsIcon = (userId: any) =>
        canEditUserDetails(userId) ? 'pi pi-pencil' : 'pi pi-eye';

    const resolveUserFullName = (user: any) => {
        const fromParts = [user?.lastName, user?.firstName, user?.middleName].filter(Boolean).join(' ');
        return (user?.fullName || '').trim() || fromParts || user?.userName || '';
    };

    const usersDataWithUserLevel = (() => {
        const fromDict = (usersData || []).map((userData: any) => ({
            ...userData,
            userLevel: 0,
            fullName: resolveUserFullName(userData)
        }));

        const byId = new Map<string, any>(fromDict.map((user: any) => [String(user.userId), user]));

        (qualificationData?.users || []).forEach((assignedUser: any) => {
            const key = String(assignedUser.userId);
            const existing = byId.get(key);
            if (existing) {
                byId.set(key, {
                    ...existing,
                    userLevel: assignedUser.userLevel ?? existing.userLevel ?? 0,
                    fullName: existing.fullName?.trim()
                        ? existing.fullName
                        : resolveUserFullName(assignedUser),
                    userName: existing.userName || assignedUser.userName,
                });
            } else {
                byId.set(key, {
                    ...assignedUser,
                    userLevel: assignedUser.userLevel ?? 0,
                    fullName: resolveUserFullName(assignedUser),
                });
            }
        });

        return Array.from(byId.values());
    })();

    const userLevels: any = {};
    qualificationData?.users?.forEach((user: { userId: string | number; userLevel: any; }) => {
        userLevels[user.userId] = user.userLevel;
    });

    usersDataWithUserLevel?.forEach((user: { userId: string | number; userLevel: any; }) => {
        if (userLevels[user.userId] !== undefined) {
            user.userLevel = userLevels[user.userId];
        }
    });

    const assignedUsers = (() => {
        const byId = new Map<string, any>();
        (qualificationData?.users || []).forEach((item: any) => {
            if (item?.userId == null) {
                return;
            }
            const key = String(item.userId);
            if (!byId.has(key)) {
                byId.set(key, item);
            }
        });
        return Array.from(byId.values());
    })();

    const filteredUsers = assignedUsers;
    const isUsersIdPresent = !!assignedUsers.length;

    const userItems = assignedUsers.map((companiesItem: any, index: number) => {
        const userItem = usersDataWithUserLevel?.find(
            (user: any) => sameUserId(user.userId, companiesItem.userId)
        ) || companiesItem;
        const userLevelError = errorFields?.users?.campaignUsersWithoutLevel?.find((user: { userId: any; }) => user.userId === companiesItem.userId);
        const displayName = resolveUserFullName(userItem);

        return (
            <div key={userItem.userId || index} className="flex align-items-center mb-2">
                {canOpenUserDetails(userItem.userId) && <Button
                    className="max-w-4rem p-button-secondary mr-3"
                    style={{minWidth: '40px'}}
                    loading={detailsLoading && currentUserId === userItem.userId}
                    disabled={!archiveTableToggle || detailsLoading}
                    icon={userDetailsIcon(userItem.userId)}
                    onClick={() => {
                        setCurrentUserId(userItem.userId);
                        dispatch(getUserDetails(userItem.userId, jwtToken?.jwtToken, true));
                        dispatch(setEditType(canEditUserDetails(userItem.userId)));
                    }}/>}
                <div className="field col-12 md:col-8 flex align-items-center m-0">
                    {displayName}
                </div>
                <InputNumber
                    ref={inputRef}
                    value={userItem?.userLevel}
                    className={classNames('', {'p-invalid': errorFields?.users && userLevelError})}
                    disabled={!archiveTableToggle || !editType}
                    min={0} max={1000}
                    useGrouping={false}
                    onChange={(e) => dispatch(changeQualificationGroupUserLevel({campaignId: qualificationData?.id, userId: userItem.userId, userLevel: e.value}))}
                />
                <Button name={userItem.userId}
                        onClick={() => {
                            dispatch(deleteQualificationGroupUser(userItem.userId));
                        }}
                        icon="pi pi-times"
                        className="delete-user p-button-secondary p-button-rounded p-button-text field col-12 md:col-2"
                        disabled={!archiveTableToggle || !editType}
                />
            </div>
        );
    })

    const usersDataItemTemplate = (option: any) => resolveUserFullName(option);

    const campaignUsers = assignedUsers.map((companiesItem: any) => {
        const user = usersDataWithUserLevel?.find((userItem: any) => sameUserId(userItem.userId, companiesItem.userId));
        if (user) {
            return user;
        }
        const {skillGroupId, ...userWithoutCampaignId} = companiesItem;
        return {
            ...userWithoutCampaignId,
            lastName: companiesItem.lastName,
            firstName: companiesItem.firstName,
            middleName: companiesItem.middleName,
            userName: companiesItem.userName,
            fullName: resolveUserFullName(companiesItem),
            userId: companiesItem.userId,
            isActive: companiesItem.isActive
        }
    })

    const changeQualificationUsers = (e: any) => {
        const updatedEValue = mergePreservedSelectedItems(
            e.value || [],
            qualificationData?.users,
            (item: any) => item?.userId,
            isUserInCatalog,
        );

        dispatch(addQualificationGroupUser({campaignId: selectedCompanyId, userId: updatedEValue}))
    }

    return (
        <div className="users">
            <div className="grid p-fluid">
                <div className="field col-12 md:col-5">
                    <h6>Користувачі:</h6>

                    <MultiSelect
                        appendTo="self"
                        value={campaignUsers}
                        options={usersDataWithUserLevel}
                        className={classNames('', {'p-invalid': errorFields?.users})}
                        onChange={(e) => {
                            changeQualificationUsers(e);
                        }}
                        itemTemplate={usersDataItemTemplate}
                        placeholder="Оберіть користувачів"
                        filter
                        dataKey="userId"
                        optionLabel="fullName"
                        selectedItemsLabel={isUsersIdPresent ? `Користувачив обрано: ${filteredUsers?.length}` : ""}
                        maxSelectedLabels={0}
                        scrollHeight="250px"
                        disabled={!archiveTableToggle || !editType}
                    />
                    {
                        assignedUsers.length ?
                            <div className="users-list">
                                {userItems}
                                <div className="grid col-12 md:col-12">
                                    {/*{errorFields?.users && <div className="col-12 md:col-12 p-0 m-0 pl-2 mb-2 w-full"><span className="title p-error text-sm">{errorFields?.users?.users}</span></div>}*/}
                                </div>
                            </div>
                            :
                            <>

                                {
                                    errorFields?.users ? <span className={classNames('', {'block title p-error text-sm mt-4': errorFields?.users})}>Користувачив не обрано</span> :
                                        <span className={classNames('no-users-message')}>Користувачив не обрано</span>
                                }
                            </>

                    }
                </div>
            </div>
            <UserListDialog/>
        </div>
    )
}

export default QualificationGroupDialogUsers;
